import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CartState, CartAction, CartItem, Product, ProductVariant } from '../types';

const STORAGE_KEY = 'ndms-cart';

function loadCartItems(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive shape check — drop anything that doesn't look like a CartItem
    return parsed.filter(
      (i: any) =>
        i && typeof i === 'object' &&
        typeof i.cartItemId === 'string' &&
        i.product && typeof i.product.productId === 'string' &&
        i.variant && typeof i.variant.variantId === 'string' &&
        typeof i.quantity === 'number' && i.quantity > 0
    );
  } catch {
    return [];
  }
}

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
  addItem: (product: Product, variant: ProductVariant) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  totalItems: number;
  totalPrice: number;
} | null>(null);

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(
        (i) => i.product.productId === action.product.productId && i.variant.variantId === action.variant.variantId
      );
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.cartItemId === existing.cartItemId
              ? { ...i, quantity: Math.min(i.quantity + 1, i.variant.stock.quantity) }
              : i
          ),
        };
      }
      const newItem: CartItem = {
        cartItemId: uuidv4(),
        product: action.product,
        variant: action.variant,
        quantity: 1,
      };
      return { ...state, items: [...state.items, newItem] };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => i.cartItemId !== action.cartItemId) };
    case 'UPDATE_QUANTITY':
      if (action.quantity <= 0) {
        return { ...state, items: state.items.filter((i) => i.cartItemId !== action.cartItemId) };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i.cartItemId === action.cartItemId ? { ...i, quantity: action.quantity } : i
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'OPEN_CART':
      return { ...state, isOpen: true };
    case 'CLOSE_CART':
      return { ...state, isOpen: false };
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };
    default:
      return state;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, undefined as unknown as CartState, () => ({
    items: loadCartItems(),
    isOpen: false,
  }));

  // Persist items (not isOpen) so the cart survives refreshes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch {
      // quota / private mode — cart just won't persist
    }
  }, [state.items]);

  const addItem = (product: Product, variant: ProductVariant) =>
    dispatch({ type: 'ADD_ITEM', product, variant });
  const removeItem = (cartItemId: string) => dispatch({ type: 'REMOVE_ITEM', cartItemId });
  const updateQuantity = (cartItemId: string, quantity: number) =>
    dispatch({ type: 'UPDATE_QUANTITY', cartItemId, quantity });
  const clearCart = () => dispatch({ type: 'CLEAR_CART' });
  const openCart = () => dispatch({ type: 'OPEN_CART' });
  const closeCart = () => dispatch({ type: 'CLOSE_CART' });

  const totalItems = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = state.items.reduce(
    (sum, i) => sum + i.quantity * (i.variant.priceInCents || 0),
    0
  );

  return (
    <CartContext.Provider
      value={{ state, dispatch, addItem, removeItem, updateQuantity, clearCart, openCart, closeCart, totalItems, totalPrice }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
